import { z } from 'zod';
import { Method, ProtocolLevel, Sex, Side } from './types.js';

/**
 * O objeto que o engine de validacao recebe: uma ficha de animal em preenchimento.
 *
 * E deliberadamente uma estrutura de dados chapada, sem referencia a Dexie nem ao
 * Postgres. O app monta isso a partir do estado local; o job noturno monta a partir
 * do banco. Os dois rodam exatamente o mesmo engine.
 */

/** Uma observacao: um trait, um lado, um valor. */
export const Observation = z.object({
  trait: z.string(),
  side: Side,
  value: z.number().nullable(),
  /** Marcado quando o item nao se aplica ao animal (coluna NA da folha 5). */
  notApplicable: z.boolean().default(false),
  /** Hora da observacao. Obrigatoria para PE_ABD. */
  observedAt: z.iso.datetime().nullable().default(null),
  instrumentId: z.string().nullable().default(null),
});
export type Observation = z.infer<typeof Observation>;

/**
 * Uma sessao de medicao: um avaliador, uma replica, um metodo.
 * A rodada normal e replicateNo 1. Os 10% sorteados para dupla medicao ganham
 * uma segunda sessao com outro avaliador. O teste de repetibilidade da Fase 0
 * (3 avaliadores x 2 vezes) sao seis sessoes — sem nenhuma mudanca de schema.
 */
export const MeasurementSession = z.object({
  evaluatorId: z.string(),
  recorderId: z.string().nullable().default(null),
  replicateNo: z.int().min(1),
  method: Method,
  observations: z.array(Observation),
});
export type MeasurementSession = z.infer<typeof MeasurementSession>;

/** Estacao 1 — condicao e manejo. Vira colunas fixas em animal_management. */
export const Management = z.object({
  weightKg: z.number().nullable().default(null),
  weightMethod: z.enum(['balanca', 'fita']).nullable().default(null),
  bcsHenneke: z.int().min(1).max(9).nullable().default(null),
  lastFeedTime: z.string().nullable().default(null),
  waterAccess: z.enum(['livre', 'restrita']).nullable().default(null),
  arrivedFarmGt30d: z.boolean().nullable().default(null),

  femaleStatus: z.enum(['vazia', 'prenhe', 'parida', 'lactando', 'doadora']).nullable().default(null),
  gestationMonths: z.number().nullable().default(null),
  daysPostpartum: z.number().nullable().default(null),

  maleStatus: z.enum(['em_reproducao', 'fora_de_reproducao']).nullable().default(null),
  coversLastWeek: z.int().nullable().default(null),

  trainingStatus: z.enum(['sem_trabalho', 'em_doma', 'em_trabalho', 'preparo_exposicao']).nullable().default(null),
  trainingMonths: z.number().nullable().default(null),

  lastShoeingDate: z.iso.date().nullable().default(null),
  shodFl: z.boolean().default(false),
  shodFr: z.boolean().default(false),
  shodHl: z.boolean().default(false),
  shodHr: z.boolean().default(false),
  barefoot: z.boolean().default(false),
  hoofCondition: z.enum(['normais', 'achinelados', 'encastelados']).nullable().default(null),

  fitness: z.enum(['apto', 'claudicacao', 'lesao']).nullable().default(null),
  lamenessLimb: z.enum(['AE', 'AD', 'PE', 'PD']).nullable().default(null),
  /** Escala AAEP 0-5. Qualquer grau >= 1 torna o animal inapto para a Estacao 4. */
  lamenessAaep: z.int().min(0).max(5).nullable().default(null),

  /**
   * Lacuna identificada na analise da ficha v1.0: a Fase 4 do projeto preve
   * genotipagem do DMRT3, e coletar pelo agora custa quase nada. Voltar a 300
   * haras daqui a quatro anos custa o projeto.
   */
  dnaSampleCollected: z.boolean().default(false),
  dnaTubeId: z.string().nullable().default(null),
});
export type Management = z.infer<typeof Management>;

/** A ficha inteira, como o engine a enxerga. */
export const AssessmentSubject = z.object({
  level: ProtocolLevel,
  sex: Sex,
  birthDate: z.iso.date(),
  assessedAt: z.iso.datetime(),
  sampledForDoubleMeasure: z.boolean().default(false),
  /** Verdadeiro quando a Estacao 4 foi realizada (ou registrada em papel). */
  hasGaitData: z.boolean().default(false),
  sessions: z.array(MeasurementSession),
  management: Management,
});
export type AssessmentSubject = z.infer<typeof AssessmentSubject>;
